import { List, ListItem, ListItemText, ListSubheader, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme } from "@material-ui/core";
import React, { Fragment } from "react";

import { MoneyValue } from "models/MoneyValue";
import { Nullable } from "models/Nullable";
import { OfferRoom } from "models/OfferRoom";
import { Rooms } from "models/Rooms";
import { isEmpty } from "Util/isEmpty";

import { roomIcons } from "./OfferRoomCreationScreen";

const moneyFormatter = new Intl.NumberFormat('no-no', {
    style: 'currency',
    currency: 'NOK',
    //TODO: Fix Safari bug
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
})

export function formatMoney(value: number) {
    return moneyFormatter.format(value).replaceAll('kr', '').replaceAll('NOK', '') + ',-';
}

const useBoldStyle = makeStyles((theme: Theme) => ({
    bold: {
        fontWeight: 'bold',
    },
    tableGroupHeader: {
        backgroundColor: theme.palette.action.disabledBackground,
    }
}));


export type TTableRowData = {
    priceFieldId: string
    priceFieldName?: Nullable<string>
    priceFieldCount: number
    priceFieldPrice: MoneyValue
}

export type TTableGroupData = {
    groupId: string
    roomId: string
    groupName: string
    rows: TTableRowData[]
}

type Props = {
    groups: TTableGroupData[]
    discount?: Nullable<number>
    manualPrice: Nullable<number>
    offerRoomComments?: { [offerRoomId: string]: string }
    setOfferRoomComments?: (value: { [offerRoomId: string]: string }) => void
    offerRoomNames?: { [offerRoomId: string]: string }
    setOfferRoomNames?: (value: { [offerRoomId: string]: string }) => void
    itemsSubheader?: string
    hideUnitPrices: boolean
}

export function getSubTotal(offerRooms: OfferRoom[]) {
    return Math.max(Object.values(offerRooms).reduce((total, offerRoom) => (
        total + Object.values(offerRoom.priceFields)
            .reduce((offerRoomTotal, roomPriceField) => offerRoomTotal + ((roomPriceField?.priceField.price) * (roomPriceField.count)), 0)
    ), 0), 0)
}

export function SummaryList({ groups, discount, manualPrice, offerRoomComments, setOfferRoomComments, offerRoomNames, setOfferRoomNames, itemsSubheader, hideUnitPrices }: Props) {
    const totalPrice = manualPrice ?? Math.max((groups.reduce((total, { rows }) => (
        total + rows.reduce((groupTotal, { priceFieldCount, priceFieldPrice }) => groupTotal + (priceFieldPrice * priceFieldCount), 0)
    ), 0)) - (discount || 0), 0);


    return (
        <List dense>
            <ListSubheader>
                Generelt
            </ListSubheader>
            {discount ? (
                <ListItem divider>
                    <ListItemText primary="Rabatt" secondary={formatMoney(discount)} />
                </ListItem>
            ) : null}
            <ListItem>
                <ListItemText
                    primary="Totalt ekskl. mva."
                    secondary={formatMoney(totalPrice)}
                />
            </ListItem>
            {itemsSubheader && <ListSubheader>{itemsSubheader}</ListSubheader>}
            {groups.map(({ rows, groupName, groupId, roomId }) => {
                const roomName = offerRoomNames?.[groupId] || '';
                const roomComment = offerRoomComments?.[groupId] || '';
                const roomTag = Rooms.FlatEnumList.find((tag) => Rooms.Flat[tag].id === roomId)
                const roomIcon = roomTag && roomIcons[roomTag]
                return ([
                    groupName ? (
                        <Fragment key={groupName}>
                            <ListSubheader color="primary">
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    {roomIcon && React.createElement(roomIcon, { fontSize: 'large' })}
                                    {roomIcon && <div style={{ marginRight: 1 }} ></div>}
                                    {roomName || groupName}
                                </div>
                            </ListSubheader>
                            <ListItem>
                                {setOfferRoomComments ? (

                                    <TextField
                                        type="text"
                                        variant="outlined"
                                        value={roomComment}
                                        onChange={e => {
                                            const { [groupId]: current, ...rest } = offerRoomComments || {}
                                            const comment = String(e.target.value)
                                            setOfferRoomComments?.(isEmpty(comment) ? rest : {
                                                ...rest,
                                                [groupId]: comment,
                                            })
                                        }}
                                        placeholder="Kommentar på sone"
                                        fullWidth
                                    />
                                ) : (
                                    roomComment ? (
                                        <ListItemText primary="Kommentar" secondary={roomComment} />
                                    ) : null
                                )}
                            </ListItem>
                        </Fragment>
                    ) : null,
                    rows.map(({ priceFieldName, priceFieldCount, priceFieldPrice, priceFieldId }) => (
                        <ListItem key={groupId + priceFieldId}>
                            <ListItemText
                                primary={priceFieldName}
                                secondary={priceFieldCount + (hideUnitPrices ? ' stk' : ' x ' + formatMoney(priceFieldPrice) + ' = ' + formatMoney(priceFieldPrice * priceFieldCount))}
                            />
                        </ListItem>
                    ))
                ])
            })}
        </List>
    );
}
export function SummaryTable({ groups, discount }: Props) {
    const firstColSpan = 5;
    const classes = useBoldStyle();

    const totalPrice = Math.max((groups.reduce((total, { rows }) => (
        total + rows.reduce((groupTotal, { priceFieldCount, priceFieldPrice }) => groupTotal + (priceFieldPrice * priceFieldCount), 0)
    ), 0)) - (discount || 0), 0);

    return (
        <TableContainer>
            <Table aria-label="basic summary table" size="small">
                <TableHead>
                    <TableRow>
                        <TableCell align="left" className={classes.bold} colSpan={firstColSpan}></TableCell>
                        <TableCell align="left" className={classes.bold}>Antall</TableCell>
                        <TableCell align="left" className={classes.bold}>Stykkpris</TableCell>
                        <TableCell align="left" className={classes.bold}>Pris</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map(({ rows, groupName, groupId }) => ([
                        groupName ? (
                            <TableRow key={groupId} className={classes.tableGroupHeader}>
                                <TableCell variant="head" align="left" colSpan={firstColSpan + 3} className={classes.bold}>{groupName}</TableCell>
                            </TableRow>
                        ) : null,
                        rows.map(({ priceFieldName, priceFieldCount, priceFieldPrice, priceFieldId }) => (
                            <TableRow key={groupId + priceFieldId}>
                                <TableCell component="th" scope="row" colSpan={firstColSpan}>{priceFieldName}</TableCell>
                                <TableCell align="right">{priceFieldCount}</TableCell>
                                <TableCell align="right">{formatMoney(priceFieldPrice)}</TableCell>
                                <TableCell align="right">{formatMoney(priceFieldPrice * priceFieldCount)}</TableCell>
                            </TableRow>
                        ))
                    ]))}
                    {discount ? (
                        <TableRow>
                            <TableCell align="left" colSpan={firstColSpan + 2} >Rabatt</TableCell>
                            <TableCell align="right">-{formatMoney(discount)}</TableCell>
                        </TableRow>
                    ) : null}
                    <TableRow>
                        <TableCell align="left" colSpan={firstColSpan + 2} className={classes.bold}>Totalt ekskl. mva.</TableCell>
                        <TableCell align="right" className={classes.bold}>{formatMoney(totalPrice)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
}
